import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBookOpen,
  faChalkboardTeacher,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import { APISD } from "../../../../../../config/apisd";
import ToastError from "../../../../../NotificationToast/ToastError";
import "./DetailEkskulPage.css";

const cleanStorageValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === "undefined" ||
    value === "null"
  ) {
    return "";
  }

  return String(value)
    .replace(/^['"]|['"]$/g, "")
    .trim();
};

const toDateInputValue = (value) => {
  if (!value) return "";

  const normalized = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized;
  }

  if (/^\d{4}-\d{2}-\d{2}[T\s]/.test(normalized)) {
    return normalized.slice(0, 10);
  }

  return "";
};

const toApiNumber = (value) => {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue)
    ? value
    : numberValue;
};

const getScoreId = (student) => {
  const scoreId = cleanStorageValue(
    student?.score_id ||
      student?.ysb_ekstrakurikuler_score_id ||
      student?.id_score
  );

  return scoreId || "default";
};

const extractScoreIdFromResponse = (response) => {
  const candidates = [
    response?.data?.data?.score_id,
    response?.data?.data?.ysb_ekstrakurikuler_score_id,
    response?.data?.data?.id_score,
    response?.data?.data?.id,

    response?.data?.score_id,
    response?.data?.ysb_ekstrakurikuler_score_id,
    response?.data?.id_score,
    response?.data?.id,
  ];

  const found = candidates.find((item) => {
    const value = cleanStorageValue(item);

    return value && value !== "default";
  });

  return found ? cleanStorageValue(found) : "";
};

const getLoginUserName = () => {
  const nameAdmin = cleanStorageValue(
    localStorage.getItem("name-admin")
  );

  if (nameAdmin) {
    return nameAdmin;
  }

  const directKeys = [
    "username",
    "nama_lengkap",
    "name",
    "nama",
    "user_name",
    "name_user",
  ];

  for (const key of directKeys) {
    const value = cleanStorageValue(
      localStorage.getItem(key)
    );

    if (value) {
      return value;
    }
  }

  const objectKeys = [
    "user",
    "user_data",
    "data_user",
    "auth_user",
  ];

  for (const key of objectKeys) {
    try {
      const raw = localStorage.getItem(key);

      if (!raw) continue;

      const user = JSON.parse(raw);

      const candidates = [
        user?.username,
        user?.nama_lengkap,
        user?.name,
        user?.nama,
        user?.user_name,
        user?.name_user,

        user?.teacher?.username,
        user?.teacher?.nama_lengkap,
        user?.teacher?.name,

        user?.data?.username,
        user?.data?.nama_lengkap,
        user?.data?.name,
        user?.data?.nama,

        user?.data?.user?.username,
        user?.data?.user?.nama_lengkap,
        user?.data?.user?.name,

        user?.data?.teacher?.username,
        user?.data?.teacher?.nama_lengkap,
        user?.data?.teacher?.name,
      ];

      const found = candidates.find((item) =>
        cleanStorageValue(item)
      );

      if (found) {
        return cleanStorageValue(found);
      }
    } catch (error) {
      // abaikan
    }
  }

  return "User Login";
};

const getLoginUserId = () => {
  const directKeys = [
    "user_id",
    "id_user",
    "id-user",
    "user-id",
    "userId",
    "id-admin",
    "id_admin",
    "ysb_user_id",
  ];

  for (const key of directKeys) {
    const value = cleanStorageValue(
      localStorage.getItem(key)
    );

    if (value) {
      return value;
    }
  }

  const objectKeys = [
    "user",
    "user_data",
    "data_user",
    "auth_user",
  ];

  for (const key of objectKeys) {
    try {
      const raw = localStorage.getItem(key);

      if (!raw) continue;

      const user = JSON.parse(raw);

      const candidates = [
        user?.user_id,
        user?.id_user,
        user?.id,
        user?.ysb_user_id,

        user?.data?.user_id,
        user?.data?.id_user,
        user?.data?.id,
        user?.data?.ysb_user_id,

        user?.data?.user?.user_id,
        user?.data?.user?.id_user,
        user?.data?.user?.id,
        user?.data?.user?.ysb_user_id,
      ];

      const found = candidates.find((item) =>
        cleanStorageValue(item)
      );

      if (found) {
        return cleanStorageValue(found);
      }
    } catch (error) {
      // abaikan
    }
  }

  return "";
};

const getDetailData = (responseData) => {
  if (
    responseData?.data?.data &&
    !Array.isArray(responseData.data.data)
  ) {
    return responseData.data.data;
  }

  if (
    responseData?.data &&
    !Array.isArray(responseData.data)
  ) {
    return responseData.data;
  }

  return {};
};

export default function DetailEkskulPage({
  selectedMatpel,
  selectedAcademicYearId,
  selectedSemesterId,
  getToken,
  getBranchId,
  getSchoolId,
  onBack,
}) {
  const [isFetching, setIsFetching] = useState(true);
  const [detailData, setDetailData] = useState({});
  const [selectedKelasId, setSelectedKelasId] =
    useState("");
  const [studentInputs, setStudentInputs] = useState({});

  /*
   * Ref ini menyimpan value terbaru dari form.
   * Jadi tidak perlu menunggu render React.
   */
  const studentInputsRef = useRef({});

  /*
   * Menyimpan score_id terbaru masing-masing siswa.
   *
   * Kalau awalnya default:
   * default -> CREATE
   *
   * Setelah API memberikan ID baru:
   * UUID -> UPDATE
   */
  const scoreIdRef = useRef({});

  /*
   * Queue request per siswa.
   *
   * tanggal mulai -> tanggal akhir -> nilai
   *
   * request tidak sama-sama memakai /default.
   */
  const saveQueueRef = useRef({});

  const loginUserName = useMemo(
    () => getLoginUserName(),
    []
  );

  const ekstrakurikulerId =
    selectedMatpel?.ysb_matpel_id ||
    selectedMatpel?.id ||
    "";

  useEffect(() => {
    document.title = selectedMatpel?.nama_matpel
      ? `${selectedMatpel.nama_matpel} - Ekstrakurikuler`
      : "Input Nilai";
  }, [selectedMatpel]);

  /*
   * =========================================================
   * GET DETAIL
   * =========================================================
   */

  useEffect(() => {
    const fetchDetail = async () => {
      if (!ekstrakurikulerId) {
        setDetailData({});
        setIsFetching(false);
        return;
      }

      setIsFetching(true);

      try {
        const token = getToken();

        const response = await APISD.request({
          method: "GET",

          url: `/api/ekstrakurikuler-scores/input-scores/${ekstrakurikulerId}`,

          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },

          params: {
            ysb_academic_year_id:
              selectedAcademicYearId,
            ysb_semester_id: selectedSemesterId,
            ysb_branch_id: getBranchId(),

            ysb_school_id: getSchoolId(),
             
          },
        });

        setDetailData(
          getDetailData(response?.data)
        );
      } catch (error) {
        setDetailData({});

        ToastError.fire({
          icon: "error",

          title:
            error?.response?.data?.message ||
            "Gagal mengambil input nilai ekstrakurikuler",
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchDetail();
  }, [
    ekstrakurikulerId,
    selectedAcademicYearId,
    getToken,
    getBranchId,
    getSchoolId,
  ]);

  /*
   * =========================================================
   * GRADE LEVEL
   * =========================================================
   */

  const visibleGradeLevels = useMemo(() => {
    const gradeLevels = Array.isArray(
      detailData?.grade_levels
    )
      ? detailData.grade_levels
      : [];

    const selectedGradeIds = Array.isArray(
      selectedMatpel?.selected_grade_level_ids
    )
      ? selectedMatpel.selected_grade_level_ids.filter(
          Boolean
        )
      : [
          selectedMatpel?.ysb_grade_level_id,
        ].filter(Boolean);

    if (selectedGradeIds.length === 0) {
      return gradeLevels;
    }

    const filtered = gradeLevels.filter((grade) =>
      selectedGradeIds.some(
        (id) =>
          String(id) ===
          String(
            grade?.ysb_grade_level_id || ""
          )
      )
    );

    return filtered.length > 0
      ? filtered
      : gradeLevels;
  }, [detailData, selectedMatpel]);

  /*
   * =========================================================
   * LIST KELAS
   * =========================================================
   */

  const kelasList = useMemo(() => {
    return visibleGradeLevels.flatMap((grade) => {
      const kelas = Array.isArray(
        grade?.kelas
      )
        ? grade.kelas
        : [];

      return kelas.map((item) => ({
        ...item,

        ysb_grade_level_id:
          grade?.ysb_grade_level_id || "",

        name_level:
          grade?.name_level || "",
      }));
    });
  }, [visibleGradeLevels]);

  /*
   * =========================================================
   * AUTO PILIH KELAS PERTAMA
   * =========================================================
   */

  useEffect(() => {
    if (kelasList.length === 0) {
      setSelectedKelasId("");
      return;
    }

    const stillExists = kelasList.some(
      (kelas) =>
        String(
          kelas?.ysb_kelas_id || ""
        ) === String(selectedKelasId)
    );

    if (!stillExists) {
      setSelectedKelasId(
        kelasList[0]?.ysb_kelas_id || ""
      );
    }
  }, [kelasList, selectedKelasId]);

  /*
   * =========================================================
   * KELAS TERPILIH
   * =========================================================
   */

  const selectedKelas = useMemo(() => {
    return (
      kelasList.find(
        (kelas) =>
          String(
            kelas?.ysb_kelas_id || ""
          ) === String(selectedKelasId)
      ) ||
      kelasList[0] ||
      null
    );
  }, [kelasList, selectedKelasId]);

  /*
  * =========================================================
  * PEMBINA KELAS TERPILIH
  * =========================================================
  */

  const pembinaList = useMemo(() => {
    const pembina = Array.isArray(
      selectedKelas?.pembina
    )
      ? selectedKelas.pembina
      : [];

    return pembina
      .map((item) => item?.nama_pembina)
      .filter(Boolean);
  }, [selectedKelas]);

  /*
   * =========================================================
   * SEMESTER TERPILIH
   * =========================================================
   */

  const activeSemester = useMemo(() => {
    const semesters = Array.isArray(
      selectedKelas?.semesters
    )
      ? selectedKelas.semesters
      : [];

    return (
      semesters.find(
        (semester) =>
          String(
            semester?.ysb_semester_id ||
              semester?.id ||
              ""
          ) ===
          String(selectedSemesterId || "")
      ) || null
    );
  }, [
    selectedKelas,
    selectedSemesterId,
  ]);

  /*
   * =========================================================
   * SISWA
   * =========================================================
   */

  const students = useMemo(() => {
    const rows = Array.isArray(
      activeSemester?.students
    )
      ? activeSemester.students
      : [];

    return [...rows].sort((a, b) =>
      String(
        a?.nama_lengkap || ""
      ).localeCompare(
        String(b?.nama_lengkap || ""),
        "id",
        {
          sensitivity: "base",
          numeric: true,
        }
      )
    );
  }, [activeSemester]);

  /*
   * =========================================================
   * KEY SISWA
   * =========================================================
   */

  const getStudentKey = (
    student,
    index = 0
  ) =>
    String(
      student?.ysb_ekstrakurikuler_student_id ||
        student?.ysb_student_id ||
        student?.nisn ||
        `student-${index}`
    );

  /*
   * =========================================================
   * MASUKKAN DATA API KE FORM
   * =========================================================
   */

  useEffect(() => {
    const nextInputs = {};

    students.forEach((student, index) => {
      const studentKey =
        getStudentKey(student, index);

      const serverScoreId =
        getScoreId(student);

      /*
       * Kalau sebelumnya sudah berhasil CREATE
       * dan score_id baru sudah kita simpan,
       * jangan timpa lagi menjadi default.
       */
      const knownScoreId =
        cleanStorageValue(
          scoreIdRef.current[studentKey]
        );

      const effectiveScoreId =
        knownScoreId &&
        knownScoreId !== "default"
          ? knownScoreId
          : serverScoreId;

      scoreIdRef.current[studentKey] =
        effectiveScoreId;

      nextInputs[studentKey] = {
        score_id: effectiveScoreId,

        date_start:
          toDateInputValue(
            student?.date_start
          ),

        date_end:
          toDateInputValue(
            student?.date_end
          ),

        score_mid:
          student?.score_mid === null ||
          student?.score_mid === undefined
            ? ""
            : String(student.score_mid),

        predikat_mid:
          student?.predikat_mid === null ||
          student?.predikat_mid === undefined
            ? ""
            : String(student.predikat_mid),

        comment_mid:
          student?.comment_mid || "",

        score_final:
          student?.score_final === null ||
          student?.score_final === undefined
            ? ""
            : String(student.score_final),

        predikat_final:
          student?.predikat_final === null ||
          student?.predikat_final === undefined
            ? ""
            : String(student.predikat_final),

        comment_final:
          student?.comment_final || "",
      };
    });

    studentInputsRef.current =
      nextInputs;

    setStudentInputs(nextInputs);
  }, [students]);

  /*
   * =========================================================
   * UPDATE FORM LOKAL
   * =========================================================
   */

  const handleStudentInputChange = (
    studentKey,
    field,
    value
  ) => {
    const currentRow =
      studentInputsRef.current[
        studentKey
      ] || {};

    const nextRow = {
      ...currentRow,
      [field]: value,
    };

    /*
     * Ref langsung berubah.
     * Jadi queueAutoSave bisa langsung membaca
     * value terbaru.
     */
    studentInputsRef.current = {
      ...studentInputsRef.current,

      [studentKey]: nextRow,
    };

    setStudentInputs((prev) => ({
      ...prev,

      [studentKey]: nextRow,
    }));
  };

  /*
   * =========================================================
   * FALLBACK CARI SCORE ID BARU
   * =========================================================
   *
   * Dipakai kalau response:
   *
   * PUT /api/ekstrakurikuler-scores/default
   *
   * tidak mengembalikan score_id baru.
   *
   * Kita GET lagi data siswa, tetapi hanya mencari
   * ID-nya saja. Tidak mereset semua form.
   */

  const findCreatedScoreIdFromServer =
    async (saveContext) => {
      try {
        const token = getToken();

        const response =
          await APISD.request({
            method: "GET",

            url: `/api/ekstrakurikuler-scores/input-scores/${saveContext.ysb_matpel_id}`,

            headers: {
              Authorization: token,
              "Content-Type":
                "application/json",
            },

            params: {
              ysb_academic_year_id:
                saveContext.ysb_academic_year_id,

              ysb_branch_id:
                saveContext.ysb_branch_id,

              ysb_school_id:
                saveContext.ysb_school_id,
            },
          });

        const latestDetail =
          getDetailData(response?.data);

        const gradeLevels =
          Array.isArray(
            latestDetail?.grade_levels
          )
            ? latestDetail.grade_levels
            : [];

        for (const grade of gradeLevels) {
          /*
           * Pastikan grade level sesuai
           * dengan siswa yang sedang disimpan.
           */
          if (
            saveContext.ysb_grade_level_id &&
            String(
              grade?.ysb_grade_level_id ||
                ""
            ) !==
              String(
                saveContext.ysb_grade_level_id
              )
          ) {
            continue;
          }

          const kelasRows =
            Array.isArray(grade?.kelas)
              ? grade.kelas
              : [];

          for (const kelas of kelasRows) {
            /*
             * Pastikan kelas sesuai.
             */
            if (
              saveContext.ysb_kelas_id &&
              String(
                kelas?.ysb_kelas_id || ""
              ) !==
                String(
                  saveContext.ysb_kelas_id
                )
            ) {
              continue;
            }

            const semesters =
              Array.isArray(
                kelas?.semesters
              )
                ? kelas.semesters
                : [];

            for (
              const semester of semesters
            ) {
              const semesterId =
                semester?.ysb_semester_id ||
                semester?.id ||
                "";

              /*
               * Pastikan semester sesuai.
               *
               * Jadi score_id semester 1 tidak
               * tertukar dengan semester 2.
               */
              if (
                saveContext.ysb_semester_id &&
                String(semesterId) !==
                  String(
                    saveContext.ysb_semester_id
                  )
              ) {
                continue;
              }

              const studentRows =
                Array.isArray(
                  semester?.students
                )
                  ? semester.students
                  : [];

              const foundStudent =
                studentRows.find((item) => {
                  const sameStudentId =
                    saveContext.ysb_student_id &&
                    String(
                      item?.ysb_student_id ||
                        item?.student_id ||
                        item?.id ||
                        ""
                    ) ===
                      String(
                        saveContext.ysb_student_id
                      );

                  const sameEkskulStudentId =
                    saveContext.ysb_ekstrakurikuler_student_id &&
                    String(
                      item?.ysb_ekstrakurikuler_student_id ||
                        ""
                    ) ===
                      String(
                        saveContext.ysb_ekstrakurikuler_student_id
                      );

                  const sameNisn =
                    saveContext.nisn &&
                    String(
                      item?.nisn || ""
                    ) ===
                      String(
                        saveContext.nisn
                      );

                  return (
                    sameStudentId ||
                    sameEkskulStudentId ||
                    sameNisn
                  );
                });

              if (foundStudent) {
                const foundScoreId =
                  getScoreId(foundStudent);

                if (
                  foundScoreId &&
                  foundScoreId !==
                    "default"
                ) {
                  return foundScoreId;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error(
          "Gagal mengambil score_id terbaru:",
          error
        );
      }

      return "";
    };

  /*
   * =========================================================
   * AUTO SAVE KE API
   * =========================================================
   */

  const performAutoSave = async (
    saveContext
  ) => {
    const {
      studentKey,
      inputValue,

      ysb_academic_year_id,
      ysb_semester_id,
      ysb_matpel_id,
      ysb_student_id,
      ysb_branch_id,
      ysb_school_id,
      ysb_grade_level_id,
      ysb_kelas_id,

      user_id,
    } = saveContext;

    if (!ysb_student_id) {
      throw new Error(
        "ID siswa tidak ditemukan"
      );
    }

    if (!ysb_semester_id) {
      throw new Error(
        "Semester tidak ditemukan"
      );
    }

    if (!ysb_matpel_id) {
      throw new Error(
        "ID ekstrakurikuler tidak ditemukan"
      );
    }

    if (!ysb_kelas_id) {
      throw new Error(
        "Kelas tidak ditemukan"
      );
    }

    /*
     * =======================================================
     * TENTUKAN SCORE ID
     * =======================================================
     */

    let scoreId =
      cleanStorageValue(
        scoreIdRef.current[
          studentKey
        ] || inputValue?.score_id
      );

    /*
     * Kondisi ini terjadi kalau CREATE sebelumnya
     * berhasil dikirim tetapi response API tidak
     * memberikan score_id baru.
     *
     * Jangan pernah kembali menggunakan /default.
     * Cari ID baru terlebih dahulu.
     */
    if (
      scoreId ===
      "__resolve_score_id__"
    ) {
      const resolvedScoreId =
        await findCreatedScoreIdFromServer(
          saveContext
        );

      if (!resolvedScoreId) {
        throw new Error(
          "Score ID baru belum ditemukan. Silakan coba input kembali beberapa saat lagi."
        );
      }

      scoreId =
        resolvedScoreId;

      scoreIdRef.current[
        studentKey
      ] = resolvedScoreId;
    }

    if (!scoreId) {
      scoreId = "default";
    }

    /*
     * =======================================================
     * PAYLOAD
     * =======================================================
     */

    const payload = {
      ysb_academic_year_id,
      ysb_semester_id,
      ysb_matpel_id,
      ysb_student_id,
      ysb_branch_id,
      ysb_school_id,
      ysb_grade_level_id,
      ysb_kelas_id,

      date_start:
        inputValue?.date_start || null,

      date_end:
        inputValue?.date_end || null,

      score_mid:
        toApiNumber(
          inputValue?.score_mid
        ),

      predikat_mid:
        toApiNumber(
          inputValue?.predikat_mid
        ),

      comment_mid:
        inputValue?.comment_mid || "",

      score_final:
        toApiNumber(
          inputValue?.score_final
        ),

      predikat_final:
        toApiNumber(
          inputValue?.predikat_final
        ),

      comment_final:
        inputValue?.comment_final || "",

      published: true,

      user_id,
    };

    console.log(
      "AUTO SAVE NILAI EKSKUL",
      {
        student:
          saveContext.nama_lengkap,

        score_id: scoreId,

        url: `/api/ekstrakurikuler-scores/${scoreId}`,

        payload,
      }
    );

    /*
     * =======================================================
     * PUT
     * =======================================================
     *
     * scoreId default:
     * PUT /api/ekstrakurikuler-scores/default
     *
     * scoreId sudah UUID:
     * PUT /api/ekstrakurikuler-scores/{UUID}
     */

    const response =
      await APISD.request({
        method: "PUT",

        url: `/api/ekstrakurikuler-scores/${scoreId}`,

        headers: {
          Authorization: getToken(),
          "Content-Type":
            "application/json",
        },

        data: payload,
      });

    /*
     * =======================================================
     * SETELAH CREATE PERTAMA
     * =======================================================
     */

    if (scoreId === "default") {
      /*
       * Coba ambil ID langsung dari response PUT.
       */
      let newScoreId =
        extractScoreIdFromResponse(
          response
        );

      /*
       * Kalau response PUT tidak membawa score_id,
       * cari dari GET detail terbaru.
       */
      if (!newScoreId) {
        scoreIdRef.current[
          studentKey
        ] = "__resolve_score_id__";

        newScoreId =
          await findCreatedScoreIdFromServer(
            saveContext
          );
      }

      /*
       * Kalau tetap belum ketemu, JANGAN kembalikan
       * score_id menjadi default.
       *
       * Karena kalau kembali default, input berikutnya
       * bisa membuat record baru lagi.
       */
      if (!newScoreId) {
        scoreIdRef.current[
          studentKey
        ] = "__resolve_score_id__";

        throw new Error(
          "Data sudah dikirim, tetapi score ID baru belum ditemukan dari API."
        );
      }

      /*
       * Simpan score_id baru.
       *
       * Input berikutnya akan memakai score_id ini
       * dan otomatis menjadi UPDATE.
       */
      scoreIdRef.current[
        studentKey
      ] = newScoreId;

      /*
       * Update score_id di value form lokal juga.
       */
      const currentInput =
        studentInputsRef.current[
          studentKey
        ];

      if (currentInput) {
        const nextInput = {
          ...currentInput,

          score_id: newScoreId,
        };

        studentInputsRef.current = {
          ...studentInputsRef.current,

          [studentKey]:
            nextInput,
        };

        setStudentInputs((prev) => {
          /*
           * Kalau user keburu pindah kelas,
           * jangan masukkan data siswa lama
           * ke state kelas baru.
           */
          if (!prev[studentKey]) {
            return prev;
          }

          return {
            ...prev,

            [studentKey]: {
              ...prev[studentKey],

              score_id:
                newScoreId,
            },
          };
        });
      }
    }

    return response;
  };

  /*
   * =========================================================
   * QUEUE AUTO SAVE
   * =========================================================
   *
   * Contoh:
   *
   * 1. User pilih tanggal mulai
   *    score_id = default
   *    -> request CREATE
   *
   * 2. Sebelum request pertama selesai,
   *    user pilih tanggal akhir.
   *
   * Request kedua TIDAK langsung jalan.
   * Dia menunggu request pertama selesai.
   *
   * Setelah request pertama mendapat UUID,
   * request kedua memakai UUID tersebut.
   *
   * Jadi tidak ada dua CREATE /default.
   */

  const queueAutoSave = (
    student,
    index
  ) => {
    const studentKey =
      getStudentKey(
        student,
        index
      );

    /*
     * Snapshot value saat field selesai diinput.
     */
    const inputValue = {
      ...(
        studentInputsRef.current[
          studentKey
        ] || {}
      ),
    };

    /*
     * Snapshot semua ID penting saat save.
     *
     * Jadi aman kalau setelah input,
     * user langsung pindah kelas.
     */
    const saveContext = {
      studentKey,
      inputValue,

      ysb_academic_year_id:
        selectedAcademicYearId || "",

      ysb_semester_id:
        activeSemester
          ?.ysb_semester_id ||
        activeSemester?.id ||
        selectedSemesterId ||
        "",

      ysb_matpel_id:
        ekstrakurikulerId,

      ysb_student_id:
        student?.ysb_student_id ||
        student?.student_id ||
        student?.id ||
        "",

      ysb_ekstrakurikuler_student_id:
        student
          ?.ysb_ekstrakurikuler_student_id ||
        "",

      ysb_branch_id:
        getBranchId(),

      ysb_school_id:
        getSchoolId(),

      ysb_grade_level_id:
        selectedKelas
          ?.ysb_grade_level_id ||
        student?.ysb_grade_level_id ||
        "",

      ysb_kelas_id:
        selectedKelas
          ?.ysb_kelas_id ||
        student?.ysb_kelas_id ||
        "",

      user_id:
        getLoginUserId(),

      nisn:
        student?.nisn || "",

      nama_lengkap:
        student?.nama_lengkap || "",
    };

    /*
     * Ambil queue siswa ini.
     */
    const previousQueue =
      saveQueueRef.current[
        studentKey
      ] || Promise.resolve();

    /*
     * Request berikutnya menunggu
     * request sebelumnya selesai.
     */
const nextQueue =
  previousQueue
    .catch(() => {
      /*
       * Jika request sebelumnya gagal,
       * queue berikutnya tetap boleh lanjut.
       */
    })

    .then(async () => {
      await performAutoSave(saveContext);

      /*
       * NOTIF BERHASIL
       */
      ToastError.fire({
        toast: true,
        position: "top-end",

        icon: "success",
        iconColor: "#00c853",

        title: "Nilai berhasil disimpan",

        showConfirmButton: false,

        timer: 2000,
        timerProgressBar: true,

        width: "360px",
      });
    })

    .catch((error) => {
      console.error(
        "AUTO SAVE NILAI EKSKUL ERROR:",
        error
      );

      console.error(
        "AUTO SAVE RESPONSE:",
        error?.response?.data
      );

      /*
       * NOTIF GAGAL
       */
      ToastError.fire({
        toast: true,
        position: "top-end",

        icon: "error",
        iconColor: "#dc3545",

        title:
          error?.response?.data?.message ||
          error?.message ||
          "Gagal menyimpan nilai siswa",

        showConfirmButton: false,

        timer: 2500,
        timerProgressBar: true,

        width: "360px",
      });
    });

saveQueueRef.current[
  studentKey
] = nextQueue;
  };

  /*
   * =========================================================
   * JUMLAH SISWA PER KELAS
   * =========================================================
   */

  const getStudentCountBySemester = (
    kelas
  ) => {
    const semesters = Array.isArray(
      kelas?.semesters
    )
      ? kelas.semesters
      : [];

    const semester =
      semesters.find(
        (item) =>
          String(
            item?.ysb_semester_id ||
              item?.id ||
              ""
          ) ===
          String(
            selectedSemesterId || ""
          )
      );

    return Number(
      semester?.jumlah_siswa ??
        semester?.students?.length ??
        0
    );
  };

  /*
   * =========================================================
   * NAMA SEMESTER
   * =========================================================
   */

  const getSemesterNameByKelas = (
    kelas
  ) => {
    const semesters = Array.isArray(
      kelas?.semesters
    )
      ? kelas.semesters
      : [];

    const semester =
      semesters.find(
        (item) =>
          String(
            item?.ysb_semester_id ||
              item?.id ||
              ""
          ) ===
          String(
            selectedSemesterId || ""
          )
      );

    return (
      semester?.name_semester ||
      semester?.semester ||
      "-"
    );
  };

  const ekskulName =
    detailData?.name_matpel ||
    selectedMatpel?.nama_matpel ||
    "Ekstrakurikuler";

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <div className="body">
      {/* HEADER */}

      <div className="page-header-card">
        <div className="">
          <div className="page-header-top d-flex">
            <div className="page-title-wrap">
              <span className="page-title-icon">
                <FontAwesomeIcon
                  icon={faBookOpen}
                />
              </span>

              <div>
                <h4 className="page-title-text mb-0">
                  Input Nilai
                </h4>
              </div>
            </div>

            <div className="ml-auto">
              <button
                type="button"
                className="btn btn-light eg-back-button"
                onClick={onBack}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                />{" "}
                Kembali
              </button>
            </div>
          </div>

          <nav
            aria-label="breadcrumb"
            className="custom-breadcrumb-nav mt-4"
          >
            <ol className="custom-breadcrumb">
              <li className="custom-breadcrumb-item">
                <button
                  type="button"
                  onClick={onBack}
                  style={{
                    background:
                      "transparent",

                    border:
                      "none",

                    padding: 0,

                    margin: 0,

                    color:
                      "#3f5bea",

                    cursor:
                      "pointer",

                    fontSize:
                      "inherit",

                    fontWeight: 500,
                  }}
                >
                  Ekstrakurikuler
                </button>
              </li>

              <li className="custom-breadcrumb-separator">
                /
              </li>

              <li
                className="custom-breadcrumb-item active"
                aria-current="page"
              >
                {selectedMatpel
                  ?.nama_matpel ||
                  "Ekstrakurikuler"}
                {" - "}
                Kelas{" "}
                {selectedMatpel
                  ?.name_level ||
                  "-"}
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* CONTENT */}

      <div className="body-content">
        {isFetching ? (
          <div className="eg-loading-wrap">
            <PulseLoader
              color="#3f5bea"
              size={10}
            />
          </div>
        ) : (
          <>
            {/* INFO EKSTRAKURIKULER */}

            <div className="eg-info-card">
              <div className="eg-info-block">
                <span className="eg-info-icon">
                  <FontAwesomeIcon
                    icon={faBookOpen}
                  />
                </span>

                <div>
                  <div className="eg-info-label">
                    Ekstrakurikuler
                  </div>

                  <div className="eg-info-value">
                    {ekskulName}
                  </div>
                </div>
              </div>

              <div className="eg-info-divider" />

              <div className="eg-info-block">
                <span className="eg-info-icon">
                  <FontAwesomeIcon
                    icon={faUser}
                  />
                </span>

                <div>
                  <div className="eg-info-label">
                    Pembina
                  </div>

                  {/* <div className="eg-info-value">
                    {pembinaName}
                  </div> */}

                  <div className="eg-pembina-list">
                    {pembinaList.length > 0 ? (
                      pembinaList.map((nama, index) => (
                        <div
                          key={`${nama}-${index}`}
                          className="eg-pembina-item"
                        >
                          <span className="eg-pembina-number">
                            {index + 1}
                          </span>

                          <span className="eg-pembina-name">
                            {nama}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="eg-info-value">
                        -
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* PILIH KELAS */}

            {kelasList.length > 0 ? (
              <>
                <div className="eg-section-heading">
                  <div>
                    <h5 className="eg-section-title">
                      Pilih Kelas
                    </h5>
                  </div>
                </div>

                {/* CARD KELAS */}

                <div className="eg-class-grid">
                  {kelasList.map(
                    (kelas) => {
                      const isActive =
                        String(
                          kelas
                            ?.ysb_kelas_id ||
                            ""
                        ) ===
                        String(
                          selectedKelas
                            ?.ysb_kelas_id ||
                            ""
                        );

                      return (
                        <button
                          type="button"
                          key={
                            kelas?.ysb_kelas_id
                          }
                          className={`eg-class-card ${
                            isActive
                              ? "active"
                              : ""
                          }`}
                          onClick={() =>
                            setSelectedKelasId(
                              kelas
                                ?.ysb_kelas_id ||
                                ""
                            )
                          }
                        >
                          <span className="eg-class-icon">
                            <FontAwesomeIcon
                              icon={
                                faUsers
                              }
                            />
                          </span>

                          <span className="eg-class-content">
                            <span className="eg-class-name">
                              Kelas{" "}
                              {kelas
                                ?.name_level ||
                                "-"}{" "}
                              {kelas
                                ?.name_kelas ||
                                ""}
                            </span>

                            <span className="eg-class-count">
                              {getStudentCountBySemester(
                                kelas
                              )}{" "}
                              siswa
                            </span>
                          </span>

                          <span className="eg-class-semester">
                            Semester{" "}
                            {getSemesterNameByKelas(
                              kelas
                            )}
                          </span>
                        </button>
                      );
                    }
                  )}
                </div>

                {/* TABLE */}

                <div className="eg-table-card">
                  <div className="eg-table-header">
                    <div>
                      <h5 className="eg-table-title">
                        Kelas{" "}
                        {selectedKelas
                          ?.name_level ||
                          "-"}{" "}
                        {selectedKelas
                          ?.name_kelas ||
                          ""}{" "}
                        - Semester{" "}
                        {activeSemester
                          ?.name_semester ||
                          "-"}
                      </h5>
                    </div>

                    {/* <div className="eg-total-badge">
                      <FontAwesomeIcon
                        icon={faUsers}
                      />{" "}
                      {students.length}{" "}
                      siswa 1
                    </div> */}
                  </div>

                  <div className="eg-table-responsive">
                    <table className="eg-student-table">
                      <thead>
                        <tr>
                          <th className="eg-col-no">
                            No
                          </th>

                          <th className="eg-col-student">
                            Nama Siswa
                          </th>

                          <th className="eg-col-nisn">
                            NISN
                          </th>

                          <th className="eg-col-date">
                            Tanggal Mulai
                          </th>

                          <th className="eg-col-date">
                            Tanggal Akhir
                          </th>

                          <th className="eg-col-score">
                            Nilai Mid
                            Semester
                          </th>

                          <th className="eg-col-predicate">
                            Nilai Predikat
                            Mid Semester
                          </th>

                          <th className="eg-col-comment">
                            Komentar Mid
                            Semester
                          </th>

                          <th className="eg-col-score">
                            Nilai Akhir
                            Semester
                          </th>

                          <th className="eg-col-predicate">
                            Nilai Predikat
                            Akhir Semester
                          </th>

                          <th className="eg-col-comment">
                            Komentar Akhir
                            Semester
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {students.length >
                        0 ? (
                          students.map(
                            (
                              student,
                              index
                            ) => {
                              const studentKey =
                                getStudentKey(
                                  student,
                                  index
                                );

                              const inputValue =
                                studentInputs[
                                  studentKey
                                ] || {
                                  score_id:
                                    getScoreId(
                                      student
                                    ),

                                  date_start:
                                    "",

                                  date_end:
                                    "",

                                  score_mid:
                                    "",

                                  predikat_mid:
                                    "",

                                  comment_mid:
                                    "",

                                  score_final:
                                    "",

                                  predikat_final:
                                    "",

                                  comment_final:
                                    "",
                                };

                              return (
                                <tr
                                  key={
                                    studentKey
                                  }
                                >
                                  {/* NOMOR */}

                                  <td className="eg-col-no">
                                    <span className="eg-number-badge">
                                      {index +
                                        1}
                                    </span>
                                  </td>

                                  {/* NAMA SISWA */}

                                  <td className="eg-student-name">
                                    {student
                                      ?.nama_lengkap ||
                                      "-"}
                                  </td>

                                  {/* NISN */}

                                  <td className="eg-student-nisn">
                                    {student
                                      ?.nisn ||
                                      "-"}
                                  </td>

                                  {/* TANGGAL MULAI */}

                                  <td>
                                    <input
                                      type="date"
                                      className="eg-table-input eg-date-input"
                                      value={
                                        inputValue.date_start
                                      }
                                      onChange={(
                                        e
                                      ) => {
                                        /*
                                         * Update form lokal dahulu.
                                         */
                                        handleStudentInputChange(
                                          studentKey,
                                          "date_start",
                                          e
                                            .target
                                            .value
                                        );

                                        /*
                                         * Date langsung save
                                         * saat tanggal dipilih.
                                         */
                                        queueAutoSave(
                                          student,
                                          index
                                        );
                                      }}
                                    />
                                  </td>

                                  {/* TANGGAL AKHIR */}

                                  <td>
                                    <input
                                      type="date"
                                      className="eg-table-input eg-date-input"
                                      value={
                                        inputValue.date_end
                                      }
                                      onChange={(
                                        e
                                      ) => {
                                        handleStudentInputChange(
                                          studentKey,
                                          "date_end",
                                          e
                                            .target
                                            .value
                                        );

                                        queueAutoSave(
                                          student,
                                          index
                                        );
                                      }}
                                    />
                                  </td>

                                  {/* NILAI MID */}

                                  <td>
                                    <input
                                      type="number"
                                      step="any"
                                      className="eg-table-input eg-score-input"
                                      value={
                                        inputValue.score_mid
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "score_mid",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Nilai"
                                    />
                                  </td>

                                  {/* PREDIKAT MID */}

                                  <td>
                                    -
                                    {/* <input
                                      type="text"
                                      className="eg-table-input eg-predicate-input"
                                      value={
                                        inputValue.predikat_mid
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "predikat_mid",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Predikat"
                                    /> */}
                                  </td>

                                  {/* KOMENTAR MID */}

                                  <td>
                                    <textarea
                                      className="eg-table-input eg-comment-input"
                                      rows="2"
                                      value={
                                        inputValue.comment_mid
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "comment_mid",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Tulis komentar..."
                                    />
                                  </td>

                                  {/* NILAI FINAL */}

                                  <td>
                                    <input
                                      type="number"
                                      step="any"
                                      className="eg-table-input eg-score-input"
                                      value={
                                        inputValue.score_final
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "score_final",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Nilai"
                                    />
                                  </td>

                                  {/* PREDIKAT FINAL */}

                                  <td>
                                    -
                                    {/* <input
                                      type="text"
                                      className="eg-table-input eg-predicate-input"
                                      value={
                                        inputValue.predikat_final
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "predikat_final",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Predikat"
                                    /> */}
                                    {/* predikat */}
                                  </td>

                                  {/* KOMENTAR FINAL */}

                                  <td>
                                    <textarea
                                      className="eg-table-input eg-comment-input"
                                      rows="2"
                                      value={
                                        inputValue.comment_final
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStudentInputChange(
                                          studentKey,
                                          "comment_final",
                                          e
                                            .target
                                            .value
                                        )
                                      }
                                      onBlur={() =>
                                        queueAutoSave(
                                          student,
                                          index
                                        )
                                      }
                                      placeholder="Tulis komentar..."
                                    />
                                  </td>
                                </tr>
                              );
                            }
                          )
                        ) : (
                          <tr>
                            <td colSpan="11">
                              <div className="eg-empty-state">
                                <span className="eg-empty-icon">
                                  <FontAwesomeIcon
                                    icon={
                                      faChalkboardTeacher
                                    }
                                  />
                                </span>

                                <div>
                                  Belum ada
                                  siswa pada
                                  kelas dan
                                  semester ini.
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="eg-empty-card">
                <FontAwesomeIcon
                  icon={
                    faChalkboardTeacher
                  }
                />

                <span>
                  Belum ada data kelas
                  untuk ekstrakurikuler
                  ini.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}